import { useContext, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { HeadingText, ParagraphText, UBAppModal, UBInput } from "../../../../Libraries";
import { CloseIcon, FilterCloseIcon, ModalCloseIconNew, SearchIcon } from "../../../../assets/icons/icon.jsx";
import { LanguageContext } from "../../../../context/LanguageContext.js";
import styles from "./styles.module.css";

/**
 * Generic search + list modal. Used for both bank selection and bill selection.
 *
 * items shape: { label: string, img?: ReactNode, ...rest }
 *   - label  — display name, used for search filtering and fallback initial
 *   - img    — optional React element (pre-rendered <img> or icon JSX)
 *   - ...rest — returned as-is via onSelect(item)
 */
const SelectItemModal = ({
  open,
  onClose,
  onSelect,
  title = "",
  items = [],
  searchPlaceholder = "Search",
  noResultText = "No results found",
  id = "select_item_modal",
}) => {
  const [I18n] = useContext(LanguageContext);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((item) => (item.label || "").toLowerCase().includes(q));
  }, [items, search]);

  const handleSelect = (item) => {
    onSelect?.(item);
    onClose?.();
    setSearch("");
  };

  const handleClose = () => {
    onClose?.();
    setSearch("");
  };

  return (
    <UBAppModal
      className={styles.selectItemModal}
      id={id}
      open={open}
      header={{
        dont_show_cross_icon: true,
        handleClose,
        title: (
          <HeadingText className="fontSize20 fontWeight500 newFontColor">
            {title}
          </HeadingText>
        ),
      }}
      footer={[
        {
          data: {
            id: `${id}_close`,
            className: "ubSecondaryButton textUppercase",
            title: I18n.close || "Close",
            onClick: handleClose,
          },
        },
      ]}
    >
      <UBInput
        inputId={`${id}_search`}
        baseClassname={`w-100 ${styles.searchField}`}
        placeholder={searchPlaceholder}
        value={search}
        onChange={(_, val) => setSearch(val)}
        prefix={<SearchIcon width={16} height={16} />}
        suffix={search ? <ModalCloseIconNew width={20} height={20} /> : null}
        handleSuffix={() => setSearch("")}
        error={false}
        autoFocus
      />

      <Box className={`${styles.list} generic-scrollbar`}>
        {filtered.map((item, i) => (
          <Box key={i} className={styles.item} onClick={() => handleSelect(item)}>
            <Box className={styles.logo}>
              {item.img ? (
                item.img
              ) : (
                <span className={styles.initial}>
                  {(item.label || "?").charAt(0).toUpperCase()}
                </span>
              )}
            </Box>
            <ParagraphText className={`fontSize14 newFontColor ${styles.name}`}>
              {item.label}
            </ParagraphText>
          </Box>
        ))}
        {filtered.length === 0 && (
          <Box className={styles.noResult}>
            <ParagraphText className="fontSize14 newLightColor">
              {noResultText}
            </ParagraphText>
          </Box>
        )}
      </Box>
    </UBAppModal>
  );
};

export default SelectItemModal;
